import type { ConsoleCitation } from "../../api/types";

export type ParsedAnswer = {
  citations: ConsoleCitation[];
  markdownFormatText: string;
};

type AnswerForParse = {
  answer: string;
  citations?: ConsoleCitation[];
};

function isDuplicate(citation: ConsoleCitation, seen: ConsoleCitation[]): boolean {
  if (citation.document_id) {
    if (seen.some((item) => item.document_id === citation.document_id)) {
      return true;
    }
  }
  return seen.some(
    (item) => item.chunk_id === citation.chunk_id && item.id === citation.id,
  );
}

/** Normalize [doc1, doc2, doc3] to [doc1] [doc2] [doc3] for consistent parsing */
function normalizeCombinedCitations(text: string): string {
  return text.replace(/\[(doc\d+(?:\s*,\s*doc\d+)*)\]/g, (match) => {
    const docNums = match.match(/doc(\d+)/g) ?? [];
    return docNums.map((token) => `[${token}]`).join(" ");
  });
}

function escapeForRegex(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * GFM only nests lists when the source uses indentation or a nested leading "-".
 * Models often emit a list line ending in ":" with following lines that should be
 * sub-bullets but omit the "-". Promote those lines so react-markdown yields nested
 * ul/li and the Answer CSS can indent them.
 */
export function promoteLooseListContinuationsToNested(text: string): string {
  const lines = text.split("\n");
  const out: string[] = [];
  const listLineWithTrailingColon = /^(\s*)((?:[-*]|\d+\.)\s+)(.+\S)\s*:\s*$/;
  let i = 0;
  while (i < lines.length) {
    const line = lines[i] as string;
    const matched = line.match(listLineWithTrailingColon);
    if (!matched) {
      out.push(line);
      i += 1;
      continue;
    }
    const baseIndent = matched[1] ?? "";
    const subBulletPrefix = `${baseIndent}  - `;
    const siblingRe = new RegExp(
      `^${escapeForRegex(baseIndent)}(?:[-*]|\\d+\\.\\s)`,
    );
    out.push(line);
    i += 1;
    while (i < lines.length && lines[i] === "") {
      out.push(lines[i] as string);
      i += 1;
    }
    if (i >= lines.length) {
      continue;
    }
    if (/^\s*#+\s/.test(lines[i] as string)) {
      continue;
    }
    if (siblingRe.test(lines[i] as string)) {
      continue;
    }
    if (
      new RegExp(`^${escapeForRegex(baseIndent)}  (?:[-*]|\\d+\\.\\s)`).test(
        lines[i] as string,
      )
    ) {
      continue;
    }
    while (i < lines.length) {
      const current = lines[i] as string;
      if (current.trim() === "") {
        if (i + 1 < lines.length && siblingRe.test(lines[i + 1] as string)) {
          out.push(current);
          i += 1;
          break;
        }
        out.push(current);
        i += 1;
        continue;
      }
      if (siblingRe.test(current) || /^\s*#+\s/.test(current)) {
        break;
      }
      if (current.length > 400) {
        out.push(current);
        i += 1;
        break;
      }
      let nested = current.trim();
      nested = nested.replace(/^[-*•]\s+/, "");
      nested = nested.replace(/^\d+\.\s+/, "");
      if (nested.length === 0) {
        i += 1;
        continue;
      }
      out.push(subBulletPrefix + nested);
      i += 1;
    }
  }
  return out.join("\n");
}

export function parseAnswer(answer: AnswerForParse): ParsedAnswer {
  const supplied = answer.citations || [];
  let answerText = promoteLooseListContinuationsToNested(
    normalizeCombinedCitations(answer.answer || ""),
  );
  const citationLinks = answerText.match(/\[(doc\d\d?\d?)]/g);
  const lengthDocN = "[doc".length;
  const filteredCitations: ConsoleCitation[] = [];
  let citationReindex = 0;
  citationLinks?.forEach((link) => {
    const citationIndex = link.slice(lengthDocN, link.length - 1);
    const citation = supplied[Number(citationIndex) - 1];
    if (citation === undefined || citation === null) {
      return;
    }
    const copy: ConsoleCitation = { ...citation };
    if (!isDuplicate(copy, filteredCitations)) {
      answerText = answerText.replaceAll(link, ` ^${++citationReindex}^ `);
      copy.reindex_id = citationReindex.toString();
      filteredCitations.push(copy);
    } else {
      const matchingCitation = copy.document_id
        ? filteredCitations.find((item) => item.document_id === copy.document_id)
        : filteredCitations.find(
            (item) => item.chunk_id === copy.chunk_id && item.id === copy.id,
          );
      if (matchingCitation) {
        answerText = answerText.replaceAll(link, ` ^${matchingCitation.reindex_id}^ `);
      }
    }
  });

  if (filteredCitations.length === 0 && supplied.length) {
    let reindex = 0;
    supplied.forEach((item) => {
      const citation: ConsoleCitation = { ...item };
      if (!isDuplicate(citation, filteredCitations)) {
        citation.reindex_id = String(++reindex);
        filteredCitations.push(citation);
      }
    });
  }

  return {
    citations: filteredCitations,
    markdownFormatText: answerText,
  };
}
